import {
  Fragment,
  JSX,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import styles from './HoverCard.module.css';

interface HoverCardProps {
  children: ReactNode;
  content: ReactNode;
  className?: string;
  contentClassName?: string;
  delay?: number;
  side?: 'bottom' | 'left' | 'right' | 'top';
  align?: 'center' | 'end' | 'start';
  sideOffset?: number;
  maxHeight?: string;
}

export function HoverCard({
  children,
  content,
  className = '',
  contentClassName = '',
  delay = 200,
  side = 'top',
  align = 'center',
  sideOffset = 8,
  maxHeight = '200px',
}: HoverCardProps): JSX.Element {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !contentRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    // offsetWidth/Height, not getBoundingClientRect: the fade-in animation scales
    // the card to 95% on its first frame, and measuring that under-reports its
    // size, so the viewport clamp below lets it overshoot the edge.
    const contentRect = {
      width: contentRef.current.offsetWidth,
      height: contentRef.current.offsetHeight,
    };
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    let top = 0;
    let left = 0;

    // Calculate position based on side
    switch (side) {
      case 'top':
        top = triggerRect.top - contentRect.height - sideOffset;
        break;
      case 'bottom':
        top = triggerRect.bottom + sideOffset;
        break;
      case 'left':
        left = triggerRect.left - contentRect.width - sideOffset;
        break;
      case 'right':
        left = triggerRect.right + sideOffset;
        break;
    }

    // Calculate alignment
    if (side === 'top' || side === 'bottom') {
      switch (align) {
        case 'start':
          left = triggerRect.left;
          break;
        case 'center':
          left =
            triggerRect.left + triggerRect.width / 2 - contentRect.width / 2;
          break;
        case 'end':
          left = triggerRect.right - contentRect.width;
          break;
      }
    } else {
      switch (align) {
        case 'start':
          top = triggerRect.top;
          break;
        case 'center':
          top =
            triggerRect.top + triggerRect.height / 2 - contentRect.height / 2;
          break;
        case 'end':
          top = triggerRect.bottom - contentRect.height;
          break;
      }
    }

    // Ensure tooltip stays within viewport
    if (left < 10) left = 10;
    if (left + contentRect.width > viewport.width - 10) {
      left = viewport.width - contentRect.width - 10;
    }
    if (top < 10) top = 10;
    if (top + contentRect.height > viewport.height - 10) {
      top = viewport.height - contentRect.height - 10;
    }

    setPosition({ top, left });
  }, [side, align, sideOffset]);

  const showTooltip = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
      // Only calculate position if not already visible
      if (!isVisible) {
        calculatePosition();
      }
    }, delay);
  }, [delay, isVisible, calculatePosition]);

  const hideTooltip = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 100);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      calculatePosition();
    }
  }, [isVisible, calculatePosition]);

  return (
    <Fragment>
      <div
        ref={triggerRef}
        className={`${styles.Trigger} ${className}`}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {children}
      </div>

      {isVisible && (
        <div
          ref={contentRef}
          className={`${styles.Content} ${contentClassName}`}
          style={{
            '--hover-top': `${position.top}px`,
            '--hover-left': `${position.left}px`,
            '--hover-max-height': maxHeight,
          } as React.CSSProperties}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
        >
          {content}
        </div>
      )}
    </Fragment>
  );
}
